import { useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../api/client'
import { useSession } from '../../api/session'

const localInput=(offset=0)=>{const date=new Date(Date.now()+offset);return new Date(date.getTime()-date.getTimezoneOffset()*60000).toISOString().slice(0,16)}

const timeZones=['UTC',...Intl.supportedValuesOf('timeZone')]

type Draft = Awaited<ReturnType<typeof api.replyDrafts>>[number]
export default function ReplyDrafts({companyId,messages}:{companyId:string;messages:{message_id:string;subject:string}[]}) {
  const { user }=useSession()
  const zoneListId=useId()
  const [drafts,setDrafts]=useState<Draft[]>([])
  const [message,setMessage]=useState('')
  const [busy,setBusy]=useState(false)
  const [error,setError]=useState('')
  const [notice,setNotice]=useState('')
  const [proposeTimes,setProposeTimes]=useState(false)
  const [analysts,setAnalysts]=useState<Awaited<ReturnType<typeof api.analysts>>>([])
  const [analyst,setAnalyst]=useState('')
  const [vendorZone,setVendorZone]=useState('')
  const [windowStart,setWindowStart]=useState(()=>localInput())
  const [windowEnd,setWindowEnd]=useState(()=>localInput(14*86400000))
  const localZone=Intl.DateTimeFormat().resolvedOptions().timeZone
  useEffect(()=>{
    let live=true
    api.analysts().then(rows=>{if(live)setAnalysts(rows)}).catch(e=>{if(live)setError(e.message)})
    return()=>{live=false}
  },[])

  useEffect(()=>{
    let live=true
    const refresh=()=>api.replyDrafts(companyId).then(rows=>{if(live)setDrafts(rows)}).catch(e=>{if(live)setError(e.message)})
    void refresh();const timer=window.setInterval(refresh,5000)
    return()=>{live=false;window.clearInterval(timer)}
  },[companyId])
  return <section className="panel" style={{marginTop:'1.5rem'}}><h3>Reply drafts</h3>
    <p className="muted">Prepare questions or document requests from the open RFP fields, or propose available times when the vendor has requested a call. Review and approve the exact wording, then send the approved reply.</p>
    {error && <p className="notice crit" role="alert">{error}</p>}
    {notice && <p className="notice" role="status">{notice}</p>}
    {!!messages.length && <form className="reply" onSubmit={async e=>{
      e.preventDefault();if(!message || busy)return;setBusy(true);setError('');setNotice('')
      try{const schedule=proposeTimes?{analyst_id:analyst,time_zone:vendorZone.trim(),window_start:new Date(windowStart).toISOString(),window_end:new Date(windowEnd).toISOString()}:undefined;await api.generateReply(companyId,message,schedule);setDrafts(await api.replyDrafts(companyId));setNotice('Draft saved. Review its wording and requested fields below.')}
      catch(e){setError(e instanceof Error?e.message:'Could not prepare a reply draft.')}
      finally{setBusy(false)}
    }}><label>Reply to <select className="select" required value={message} disabled={busy} onChange={e=>setMessage(e.target.value)}><option value="">Choose an inbound message</option>{messages.map(m=><option key={m.message_id} value={m.message_id}>{m.subject}</option>)}</select></label>
      <label><input type="checkbox" checked={proposeTimes} disabled={busy} onChange={e=>setProposeTimes(e.target.checked)}/> Propose call times</label>
      {proposeTimes && <>
        <label>Analyst <select className="select" required value={analyst} disabled={busy} onChange={e=>setAnalyst(e.target.value)}><option value="">Choose an analyst</option>{analysts.map(person=><option key={person.id} value={person.id}>{person.name || person.email}</option>)}</select></label>
        <label>Vendor time zone <input className="input" list={zoneListId} required value={vendorZone} disabled={busy} maxLength={100} placeholder="e.g. America/New_York" onChange={e=>setVendorZone(e.target.value)}/><datalist id={zoneListId}>{timeZones.map(zone=><option key={zone} value={zone}/>)}</datalist></label>
        <label>Search from ({localZone}) <input className="input" type="datetime-local" required value={windowStart} disabled={busy} onChange={e=>setWindowStart(e.target.value)}/></label>
        <label>Search until ({localZone}) <input className="input" type="datetime-local" required value={windowEnd} disabled={busy} onChange={e=>setWindowEnd(e.target.value)}/></label>
        <p className="muted">Up to three available times will be shown in the vendor’s time zone. Proposals do not reserve a meeting. Availability is checked again before sending.</p>
      </>}
      <div><button className="btn secondary small" disabled={busy || !message}>{busy?'Preparing draft…':'Prepare reply draft'}</button></div>
    </form>}
    {drafts.map(draft=><details className="agent-stage" key={draft.id}><summary><span>{draft.draft.subject}</span><span className="pill">{draft.status}</span></summary>
      <p className="muted">Prepared by {draft.actor} · {new Date(draft.created_at).toLocaleString()}</p>
      {draft.draft.inquiry_context && <ReplyInquiry key={draft.draft.inquiry_context.inquiry_id} companyId={companyId} context={draft.draft.inquiry_context} />}
      {draft.delivery_error && <p className="notice crit">{draft.delivery_error}</p>}
      {['draft','approved','rejected'].includes(draft.status) ? <DraftReview key={draft.artifact+draft.status} companyId={companyId} draft={draft} saved={async()=>setDrafts(await api.replyDrafts(companyId))}/> : <p style={{whiteSpace:'pre-wrap'}}>{draft.draft.body}</p>}
      {!['draft','approved','rejected'].includes(draft.status) && draft.draft.attachments?.map(item=><p key={item.sha256}><a href={api.replyAttachmentUrl(companyId,draft.id,item.sha256)}>{item.filename}</a></p>)}
      {draft.status==='uncertain' && user?.role==='admin' && <ReconcileReply companyId={companyId} draft={draft} saved={async()=>setDrafts(await api.replyDrafts(companyId))}/>}
      {draft.status==='queued' && <CancelReply companyId={companyId} draft={draft} saved={async()=>setDrafts(await api.replyDrafts(companyId))}/>}
      {draft.draft.kind!=='call_times' && <details><summary>RFP fields included ({draft.draft.items.length})</summary><ul>{draft.draft.items.map(item=><li key={item.field_id}>{item.text}</li>)}</ul></details>}
    </details>)}
    {!drafts.length && <p className="muted">No reply drafts yet.</p>}
  </section>
}

function DraftReview({companyId,draft,saved}:{companyId:string;draft:Draft;saved:()=>Promise<void>}) {
  const [subject,setSubject]=useState(draft.draft.subject)
  const [body,setBody]=useState(draft.draft.body)
  const [reason,setReason]=useState('')
  const [busy,setBusy]=useState(false)
  const [error,setError]=useState('')
  const [history,setHistory]=useState<Awaited<ReturnType<typeof api.replyReviews>>|null>(null)
  const changed=subject!==draft.draft.subject || body!==draft.draft.body
  async function act(action:'edit'|'approve'|'reject'){
    setBusy(true);setError('')
    try{await api.reviewReply(companyId,draft.id,{action,expected_artifact:draft.artifact,reason,subject,body});await saved();setReason('');setHistory(null)}
    catch(e){setError(e instanceof Error?e.message:'Could not save review')}
    finally{setBusy(false)}
  }
  return <div className="reply">
    {error && <p className="notice crit" role="alert">{error}</p>}
    <label>Subject <input className="input" maxLength={250} value={subject} disabled={busy} onChange={e=>setSubject(e.target.value)}/></label>
    <label>Reply <textarea className="input" rows={12} maxLength={6000} readOnly={draft.draft.kind==='call_times'} value={body} disabled={busy} onChange={e=>setBody(e.target.value)}/></label>
    {draft.draft.kind==='call_times' && <p className="muted">Proposed times are checked against availability. To change them, prepare a new draft with a different search window.</p>}
    <label>Add attachment (five files, 5 MiB total)
      <input type="file" accept=".pdf,.txt,.csv,.docx,.xlsx,.png,.jpg,.jpeg" disabled={busy || changed} onChange={async e=>{
        const file=e.target.files?.[0];e.target.value='';if(!file)return
        if(file.size>5*1024*1024){setError('Attachment exceeds 5 MiB');return}
        setBusy(true);setError('')
        try{
          const data=await new Promise<string>((resolve,reject)=>{const reader=new FileReader();reader.onload=()=>resolve(String(reader.result).split(',')[1]);reader.onerror=()=>reject(new Error('Could not read attachment'));reader.readAsDataURL(file)})
          await api.replyAttachment(companyId,draft.id,{expected_artifact:draft.artifact,filename:file.name,data});await saved()
        }catch(e){setError(e instanceof Error?e.message:'Could not attach file')}
        finally{setBusy(false)}
      }}/>
    </label>
    <p className="muted">Review files for private client information before approving. Save text changes before changing attachments.</p>
    {draft.draft.attachments?.map(item=><div className="action-group" key={item.sha256}><a href={api.replyAttachmentUrl(companyId,draft.id,item.sha256)}>{item.filename} · {Math.ceil(item.size/1024)} KB</a><button className="btn small secondary" disabled={busy || changed} onClick={async()=>{
      setBusy(true);setError('')
      try{await api.replyAttachment(companyId,draft.id,{expected_artifact:draft.artifact,remove:item.sha256});await saved()}
      catch(e){setError(e instanceof Error?e.message:'Could not remove attachment')}
      finally{setBusy(false)}
    }}>Remove</button></div>)}
    <label>Review reason <input className="input" maxLength={2000} value={reason} disabled={busy} onChange={e=>setReason(e.target.value)} placeholder="Explain your edit or decision"/></label>
    <div className="action-group">
      <button className="btn small secondary" disabled={busy || !reason.trim() || !changed} onClick={()=>act('edit')}>Save changes</button>
      <button className="btn small" disabled={busy || !reason.trim() || changed || draft.status!=='draft'} onClick={()=>act('approve')}>Approve draft</button>
      {draft.status==='approved' && <button className="btn small" disabled={busy || changed} onClick={async()=>{
        setBusy(true);setError('')
        try{await api.sendReply(companyId,draft.id,draft.artifact);await saved()}
        catch(e){setError(e instanceof Error?e.message:'Could not queue reply')}
        finally{setBusy(false)}
      }}>Send approved reply</button>}
      <button className="btn small secondary" disabled={busy || !reason.trim() || changed || draft.status==='rejected'} onClick={()=>act('reject')}>Reject</button>
    </div>
    <p className="muted">Approval records this exact version. Editing clears approval. Use Send approved reply to queue delivery. Accepted means the email service accepted the message.</p>
    <details onToggle={e=>{if(e.currentTarget.open && history===null)api.replyReviews(companyId,draft.id).then(setHistory).catch(e=>setError(e.message))}}><summary>Review history</summary>
      {history?.map((item,i)=><p key={i}>{item.action} · {item.actor} · {new Date(item.created_at).toLocaleString()}<br/>{item.reason}</p>)}
      {history?.length===0 && <p>No reviews yet.</p>}
    </details>
  </div>
}


function CancelReply({companyId,draft,saved}:{companyId:string;draft:Draft;saved:()=>Promise<void>}) {
  const [reason,setReason]=useState('')
  const [busy,setBusy]=useState(false)
  const [error,setError]=useState('')
  return <form className="reply" onSubmit={async e=>{
    e.preventDefault();setBusy(true);setError('')
    try{await api.cancelReply(companyId,draft.id,draft.artifact,reason);await saved()}
    catch(e){setError(e instanceof Error?e.message:'Could not cancel queued reply')}
    finally{setBusy(false)}
  }}>
    {error && <p className="notice crit" role="alert">{error}</p>}
    <label>Reason to withdraw <input className="input" required maxLength={2000} value={reason} disabled={busy} onChange={e=>setReason(e.target.value)}/></label>
    <div><button className="btn small secondary" disabled={busy || !reason.trim()}>Cancel queued send</button></div>
    <p className="muted">Available until delivery starts. Canceling returns the reply to a draft that needs approval again.</p>
  </form>
}


function ReconcileReply({companyId,draft,saved}:{companyId:string;draft:Draft;saved:()=>Promise<void>}) {
  const [provider,setProvider]=useState('')
  const [evidence,setEvidence]=useState('')
  const [busy,setBusy]=useState(false)
  const [error,setError]=useState('')
  return <details><summary>Resolve verified SES acceptance</summary>
    <p>Check the SES record for this recipient and send time first. Recording acceptance restores the thread without sending another email. The reserved cost stays until billing is reconciled.</p>
    <form className="reply" onSubmit={async e=>{
      e.preventDefault();setBusy(true);setError('')
      try{await api.reconcileReply(companyId,draft.id,provider,evidence);await saved()}
      catch(e){setError(e instanceof Error?e.message:'Could not reconcile delivery')}
      finally{setBusy(false)}
    }}>
      {error && <p className="notice crit" role="alert">{error}</p>}
      <label>SES MessageId <input className="input" required minLength={5} maxLength={200} value={provider} disabled={busy} onChange={e=>setProvider(e.target.value)}/></label>
      <label>Evidence checked <textarea className="input" required minLength={20} maxLength={4000} rows={3} value={evidence} disabled={busy} onChange={e=>setEvidence(e.target.value)} placeholder="SES record or log reference, recipient, and send time"/></label>
      <div><button className="btn small secondary" disabled={busy || evidence.trim().length<20 || provider.trim().length<5}>Record verified acceptance</button></div>
    </form>
  </details>
}


function ReplyInquiry({companyId, context}: {companyId: string; context: {inquiry_id: string; engagement_id: string | null; binding_hash: string | null; origin_job_id: string}}) {
  const [snapshot, setSnapshot] = useState<Awaited<ReturnType<typeof api.companyInquiry>> | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)
  const pending = useRef(false)
  const panelId = useId()
  return <div style={{marginBlock: '1rem'}}>
    <p><strong>Original inquiry:</strong> {snapshot?.inquiry.engagement_name || (context.engagement_id ? 'Engagement inquiry' : 'Company inquiry')}</p>
    <p className="muted">This draft uses the original inquiry's saved package and criteria. Later company preparation does not replace that source.</p>
    <div className="action-group">
      {context.engagement_id && <Link to="/app/engagements">View engagements</Link>}
      <button className="btn secondary small" disabled={busy} aria-expanded={open} aria-controls={panelId} onClick={async () => {
        if (pending.current) return
        if (open) { setOpen(false); return }
        if (snapshot) { setOpen(true); return }
        pending.current = true; setBusy(true); setError('')
        try {
          const value = await api.companyInquiry(companyId, context.inquiry_id)
          if (value.inquiry.id !== context.inquiry_id || value.inquiry.engagement_id !== context.engagement_id) throw new Error('Saved inquiry does not match this reply draft.')
          setSnapshot(value); setOpen(true)
        } catch (e) { setError(e instanceof Error ? e.message : 'Could not load original inquiry') }
        finally { pending.current = false; setBusy(false) }
      }}>{busy ? 'Loading original inquiry…' : open ? 'Hide original inquiry package' : 'Inspect original inquiry package'}</button>
    </div>
    {error && <p className="notice crit" role="alert">{error}</p>}
    {open && snapshot && <div id={panelId} role="region" aria-label="Original reply inquiry package">
      <p>{snapshot.inquiry.engagement_name || 'Company inquiry'} · {snapshot.inquiry.channel.replace(/_/g, ' ')} · {new Date(snapshot.inquiry.created_at).toLocaleString()}</p>
      <pre className="compiled-prompt">{JSON.stringify(snapshot.package, null, 2)}</pre>
      <details><summary>Original inquiry and criteria binding</summary><pre className="compiled-prompt">{JSON.stringify({reply_context: context, inquiry: snapshot.inquiry}, null, 2)}</pre></details>
    </div>}
  </div>
}
